import type { Workflow } from "schemas/workflows";
import type { UseCaseArtworkId } from "../workflow-art/useCaseArtwork";

import { getWorkflow, resolveWorkflowSlug } from "schemas/workflows";
import * as z from "zod";

import { isUseCaseArtworkId } from "../workflow-art/useCaseArtwork";

/** The one thing a marketing use-case page sends: its own slug. */
export const ARRIVAL_WORKFLOW_SEARCH_PARAM = "marketingWorkflow";

const storageKey = "replo.marketing-workflow-arrival";

/**
 * The marketing use cases that are a Home workflow, which is what their link opens.
 * Home shows the version of it the project is offered. A use case not listed here has no counterpart
 * and arrives as an ordinary sign-up.
 */
const HOME_WORKFLOW_BY_USE_CASE: Partial<Record<UseCaseArtworkId, string>> = {
  "business-website": "services-build-site",
  "booking-page": "services-build-booking-page",
  "services-pricing-page": "services-build-services-page",
  "offer-landing-page": "services-build-landing-page",
  "saas-landing-page": "saas-build-landing-page",
  "saas-feature-page": "saas-build-feature-page",
  "saas-pricing-page": "saas-build-pricing-page",
  "store-from-scratch": "build-storefront",
  "bundle-builder": "build-bundle",
  "figma-to-page": "import-figma",
  "ad-to-lander": "shopify-build-ad-page",
  "ad-congruence-check": "shopify-build-ad-page",
  "competitor-teardown": "research-competitor-pages",
  "competitor-watch": "research-competitor-pages",
  "paid-social-funnel": "build-landing-page",
  "sale-launcher": "build-landing-page",
  "seasonal-promo": "build-landing-page",
  "campaign-pages": "build-landing-page",
  "comparison-pages": "build-landing-page",
};

type ArrivalWorkflow = {
  /** Draws the card: every marketing use case has artwork under its own slug. */
  artworkId: UseCaseArtworkId;
  workflow: Workflow;
};

/** The Home workflow a marketing use case opens, or null when it has none or the one it had is gone. */
export function getArrivalWorkflow(
  useCase: string | null,
): ArrivalWorkflow | null {
  if (!useCase || !isUseCaseArtworkId(useCase)) {
    return null;
  }
  const slug = resolveWorkflowSlug(HOME_WORKFLOW_BY_USE_CASE[useCase] ?? null);
  return slug ? { artworkId: useCase, workflow: getWorkflow(slug) } : null;
}

const ARRIVAL_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const arrivalSchema = z.object({ slug: z.string(), savedAt: z.number() });

/** Remembers the use case a marketing link named. localStorage, so onboarding in another tab still knows it. */
export function saveArrivalWorkflow(searchParams: URLSearchParams) {
  const slug = searchParams.get(ARRIVAL_WORKFLOW_SEARCH_PARAM);
  if (!getArrivalWorkflow(slug)) {
    return;
  }
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({ slug, savedAt: Date.now() }),
    );
  } catch {
    // Blocked storage only loses the preselected workflow; sign-up carries on.
  }
}

/** Forgets the marketing arrival once it has been opened, so a later onboarding in this browser does not open it again. */
export function clearArrivalWorkflow() {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Blocked storage never saved one.
  }
}

/** The workflow a visitor signed up to run from the marketing site, for a day after their link. */
export function readArrivalWorkflow(): ArrivalWorkflow | null {
  let stored: unknown = null;
  try {
    stored = JSON.parse(localStorage.getItem(storageKey) ?? "null");
  } catch {
    return null;
  }
  const arrival = arrivalSchema.safeParse(stored);
  if (
    !arrival.success ||
    Date.now() - arrival.data.savedAt >= ARRIVAL_MAX_AGE_MS
  ) {
    return null;
  }
  return getArrivalWorkflow(arrival.data.slug);
}
